import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { environment } from '../../../../environments/environment';
import { ProdutoService } from '../../../shared/services/apis/produto/produto.service';
import { LoteSaidaEstoqueModalComponent, LoteSaidaEstoqueModalData, LoteSaidaEstoqueModalItem } from '../../../shared/components/lote-saida-estoque-modal/lote-saida-estoque-modal.component';
import { SerieSaidaEstoqueModalComponent, SerieSaidaEstoqueModalData, SerieSaidaEstoqueModalItem } from '../../../shared/components/serie-saida-estoque-modal/serie-saida-estoque-modal.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { LookupService as ProdutoLookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { LookupItemStruct } from '../../../shared/services/structs/pessoa/lookup-item.struct';
import { TransferenciaEstoqueService } from '../../../shared/services/apis/suprimentos/transferencia-estoque.service';
import { TransferenciaEstoqueItemRequest } from '../../../shared/services/requests/suprimentos/transferencia-estoque-item.request';
import { TransferenciaOrquestradorService } from '../../../shared/services/apis/orquestrador-suprimentos/transferencia-orquestrador.service';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../../shared/services/mask.service';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';
import { StatusTransferenciaEnum } from '../../../shared/enums/suprimentos/status-transferencia.enum';

export interface TransferenciaItemUI {
  idProduto: number;
  descricaoProduto: string;
  skuProduto?: string;
  quantidade: number;
  custoMedio?: number;
  unidade?: string;
  estoqueOrigem?: number;
  isLoteDetalhado: boolean;
  isSerieDetalhado: boolean;
  lotes: LoteSaidaEstoqueModalItem[];
  series: SerieSaidaEstoqueModalItem[];
  erroQtd?: string;
  // Seleção de produto inline na própria linha
  produtoControl: FormControl;
  produtoPreload: any[];
}

@Component({
  selector: 'app-transferencia-estoque-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
    EmptyListComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
    SelectPesquisavelComponent,
    AbntPipe,
  ],
  templateUrl: './transferencia-estoque-cadastro.component.html',
  styleUrl: './transferencia-estoque-cadastro.component.css',
})
export class TransferenciaEstoqueCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_ControleEstoque;

  public idTransferencia: number | null = null;
  public isReadOnly = false;
  public isSaving = false;
  public isProcessando = false;
  public isLoading = false;

  public idLocalizacaoEstoqueOrigem: number | null = null;
  public idLocalizacaoEstoqueDestino: number | null = null;
  public observacoes: string = '';

  public idEmpresaAtual: number = 0;
  public nomeEmpresaAtual: string = '';

  public itens: TransferenciaItemUI[] = [];
  public localizacoes: LookupItemStruct[] = [];
  public displayedColumns = ['produto', 'sku', 'quantidade', 'unidade', 'estoqueOrigem', 'acoes'];

  // Select de produto inline por linha
  public urlProduto = `${environment.urlApiProduto}Lookup/listProdutoSelect`;

  // Filtro de produtos da listagem
  public filtroProduto = '';
  public get itensFiltrados(): TransferenciaItemUI[] {
    const t = this.filtroProduto.trim().toLowerCase();
    if (!t) return this.itens;
    return this.itens.filter(i => !i.idProduto
      || (i.descricaoProduto || '').toLowerCase().includes(t)
      || (i.skuProduto || '').toLowerCase().includes(t));
  }

  // No mobile o botão "Adicionar produto" fica acima da lista — mostrar a linha recém-adicionada
  // logo abaixo dele (mais recente primeiro) em vez de forçar rolagem até o fim da lista.
  public get itensFiltradosMobile(): TransferenciaItemUI[] {
    return [...this.itensFiltrados].reverse();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private alertService: AlertService,
    private transferenciaEstoqueService: TransferenciaEstoqueService,
    private transferenciaOrquestradorService: TransferenciaOrquestradorService,
    private produtoLookupService: ProdutoLookupService,
    private authService: AuthService,
    private maskService: MaskService,
    private produtoService: ProdutoService,
  ) { }

  // Máscara de quantidade com as casas decimais globais do usuário (token info).
  public quantidadeMask = this.maskService.getDynamicDecimalMask();

  ngOnInit(): void {
    const userInfo = this.authService.getUserInfos();
    this.idEmpresaAtual = userInfo.idEmpresa;
    this.nomeEmpresaAtual = userInfo.nomeEmpresa;

    const id = this.route.snapshot.params['id'];
    if (id) {
      this.idTransferencia = +id;
      this.carregarTransferencia();
    }
    this.carregarLookups();
  }

  private carregarLookups(): void {
    this.produtoLookupService.getLocalizacaoEstoqueSelect().subscribe({
      next: (resp) => { if (!resp?.isError) this.localizacoes = resp.list ?? []; },
    });
  }

  private carregarTransferencia(): void {
    this.isLoading = true;
    this.transferenciaEstoqueService.getById(this.idTransferencia!).subscribe({
      next: (resp) => {
        this.isLoading = false;
        if (resp?.isError) {
          this.alertService.show('Erro', resp.errorDescription, AlertType.error);
          return;
        }
        const t = resp.transferencia;
        this.idLocalizacaoEstoqueOrigem = t.idLocalizacaoEstoqueOrigem;
        this.idLocalizacaoEstoqueDestino = t.idLocalizacaoEstoqueDestino;
        this.observacoes = t.observacoes ?? '';
        this.isReadOnly = t.idStatusTransferencia !== StatusTransferenciaEnum.Pendente;
        this.itens = (t.itens ?? []).map((i: any) => ({
          idProduto: i.idProduto,
          descricaoProduto: i.descricaoProduto,
          skuProduto: i.skuProduto,
          quantidade: i.quantidade,
          custoMedio: i.custoMedio,
          unidade: i.unidade ?? 'UN',
          estoqueOrigem: i.saldoDisponivel,
          isLoteDetalhado: i.isLoteDetalhado,
          isSerieDetalhado: i.isSerieDetalhado,
          // Mapeia o retorno da API para o modelo da UI: o backend devolve `quantidade` no lote,
          // mas a UI/modal usa `quantidadeSaida`; a série precisa vir marcada (`selecionada`).
          // Suprimentos guarda só os ids — os códigos são resolvidos via lookup logo abaixo.
          lotes: (i.lotes ?? []).map((l: any) => ({
            idLote: l.idLote,
            codigoLote: '',
            quantidadeSaida: l.quantidade,
            saldoDisponivel: l.quantidade,
          })),
          series: (i.series ?? []).map((s: any) => ({
            idSerie: s.idSerie,
            codigoSerie: '',
            selecionada: true,
          })),
          produtoControl: new FormControl(i.idProduto),
          produtoPreload: [{ idProduto: i.idProduto, descricao: i.descricaoProduto }],
        }));

        this.resolverNomesProdutos();
        this.resolverCodigosLoteSerie();
        this.resolverSaldoOrigem();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a transferência.', AlertType.error);
      },
    });
  }

  // GetDetalhe (Suprimentos) não calcula saldo — Suprimentos não tem acesso a dados de estoque do
  // Produto. Busca via Lookup/produtoSaldoLocal (mesmo endpoint já usado no Inventário e no modo criar,
  // ver resolverSaldoOrigemItem()) pra preencher o "Estoque origem" também em edição.
  private resolverSaldoOrigem(): void {
    const idsUnicos = [...new Set(this.itens.map(i => i.idProduto))];
    for (const idProduto of idsUnicos) {
      const item = this.itens.find(i => i.idProduto === idProduto);
      if (item) this.resolverSaldoOrigemItem(item);
    }
  }

  private resolverNomesProdutos(): void {
    const idsUnicos = [...new Set(this.itens.map(i => i.idProduto))];

    if (!idsUnicos.length) return;

    this.produtoLookupService.getAllSelectByIds(idsUnicos).subscribe({
      next: (resp) => {
        if (!resp?.isError) {
          const produtosRetornados = resp.list ?? [];
          for (const item of this.itens) {
            const produto = produtosRetornados.find(p => p.idProduto === item.idProduto);
            if (produto) {
              item.descricaoProduto = produto.descricao;
              item.skuProduto = produto.codigoSku ?? item.skuProduto;
              item.produtoPreload = [{ idProduto: item.idProduto, descricao: produto.descricao }];
            } else if (!item.descricaoProduto) {
              item.descricaoProduto = `Produto #${item.idProduto}`;
              item.produtoPreload = [{ idProduto: item.idProduto, descricao: item.descricaoProduto }];
            }
          }
        }
      },
    });
  }

  // Resolve os códigos de lote/série via lookup (Suprimentos guarda só os ids, sem snapshot).
  private resolverCodigosLoteSerie(): void {
    const idsLote = [...new Set(this.itens.flatMap(i => i.lotes.map(l => l.idLote)))];
    if (idsLote.length) {
      this.produtoLookupService.getLoteSelectByIds(idsLote).subscribe({
        next: (resp) => {
          if (resp?.isError) return;
          const porId = new Map((resp.list ?? []).map(l => [l.idLote, l]));
          for (const item of this.itens) {
            for (const lote of item.lotes) {
              const l = porId.get(lote.idLote);
              if (l) {
                lote.codigoLote = l.codigo;
                lote.dataValidade = l.validade;
              }
            }
          }
        },
      });
    }

    const idsSerie = [...new Set(this.itens.flatMap(i => i.series.map(s => s.idSerie)))];
    if (idsSerie.length) {
      this.produtoLookupService.getSerieSelectByIds(idsSerie).subscribe({
        next: (resp) => {
          if (resp?.isError) return;
          const porId = new Map((resp.list ?? []).map(s => [s.idSerie, s.codigo]));
          for (const item of this.itens) {
            for (const serie of item.series) {
              const codigo = porId.get(serie.idSerie);
              if (codigo) serie.codigoSerie = codigo;
            }
          }
        },
      });
    }
  }

  // Adiciona uma nova linha vazia; o produto é escolhido pelo select da própria linha.
  public adicionarItem(): void {
    this.itens = [...this.itens, {
      idProduto: 0,
      descricaoProduto: '',
      skuProduto: undefined,
      quantidade: 1,
      unidade: 'UN',
      estoqueOrigem: undefined,
      isLoteDetalhado: false,
      isSerieDetalhado: false,
      lotes: [],
      series: [],
      produtoControl: new FormControl(null),
      produtoPreload: [],
    }];
  }

  // Troca/define o produto da linha pelo select. Reconfigura a linha conforme o tipo.
  public onProdutoTrocado(item: TransferenciaItemUI, sel: any): void {
    const id = sel?.idProduto;
    if (!id) return;

    if (this.itens.some(i => i !== item && i.idProduto === id)) {
      this.alertService.show('Atenção', 'Produto já adicionado à lista.', AlertType.warning);
      item.produtoControl.setValue(item.idProduto || null);
      return;
    }

    this.produtoService.getById(id).subscribe({
      next: (resp) => {
        const p = resp?.produto;
        if (resp?.isError || !p) {
          this.alertService.show('Erro', 'Não foi possível carregar o produto.', AlertType.error);
          return;
        }
        item.idProduto = p.idProduto;
        item.descricaoProduto = p.nome;
        item.skuProduto = p.codigoSku ?? undefined;
        item.unidade = 'UN';
        item.estoqueOrigem = undefined;
        item.isLoteDetalhado = p.isControlaLotes ?? false;
        item.isSerieDetalhado = p.isControlaSeries ?? false;
        item.quantidade = 1;
        item.lotes = [];
        item.series = [];
        item.erroQtd = undefined;
        item.produtoPreload = [{ idProduto: p.idProduto, descricao: p.nome }];
        this.resolverSaldoOrigemItem(item);
      },
      error: () => this.alertService.show('Erro', 'Não foi possível carregar o produto.', AlertType.error),
    });
  }

  // Saldo disponível na origem, pelo mesmo endpoint que já alimenta o modal de lote/série
  // (Lookup/produtoSaldoLocal) — produtoService.getById().localizacoesEstoque não bate com o saldo
  // real por lote/série (ex.: mostrava 0 pra um produto com 50 disponível num lote específico).
  private resolverSaldoOrigemItem(item: TransferenciaItemUI): void {
    if (!this.idLocalizacaoEstoqueOrigem) return;
    this.produtoLookupService.getProdutoSaldoLocal(item.idProduto, [this.idLocalizacaoEstoqueOrigem]).subscribe({
      next: (resp) => {
        if (resp?.isError) return;
        const local = (resp.list ?? []).find(l => l.idLocalizacaoEstoque === this.idLocalizacaoEstoqueOrigem);
        if (local) item.estoqueOrigem = local.saldoDisponivel;
      },
    });
  }

  public removerItem(item: TransferenciaItemUI): void {
    this.itens = this.itens.filter(i => i !== item);
  }

  // Badge de status do botão de lote/série: quantidade informada / necessária.
  public rastreabilidadeInformada(item: TransferenciaItemUI): number {
    if (item.isLoteDetalhado) {
      return (item.lotes ?? []).reduce((acc, l) => acc + (l.quantidadeSaida || 0), 0);
    }
    if (item.isSerieDetalhado) return item.series?.length ?? 0;
    return 0;
  }

  public rastreabilidadeStatus(item: TransferenciaItemUI): 'vazio' | 'parcial' | 'ok' {
    if (this.rastreabilidadeInformada(item) === 0) return 'vazio';
    return this.isRastreabilidadeValida(item) ? 'ok' : 'parcial';
  }

  public isRastreabilidadeValida(item: TransferenciaItemUI): boolean {
    if (item.isLoteDetalhado) {
      if (!item.lotes?.length) return false;
      const soma = item.lotes.reduce((acc, l) => acc + (l.quantidadeSaida || 0), 0);
      return soma === item.quantidade;
    }
    if (item.isSerieDetalhado) {
      return (item.series?.length ?? 0) === item.quantidade;
    }
    return true;
  }

  public abrirRastreabilidade(item: TransferenciaItemUI): void {
    if (!this.idLocalizacaoEstoqueOrigem || !item.idProduto) return;
    if (item.isLoteDetalhado) this.abrirLotesSaida(item);
    else if (item.isSerieDetalhado) this.abrirSeriesSaida(item);
  }

  private abrirLotesSaida(item: TransferenciaItemUI): void {
    const data: LoteSaidaEstoqueModalData = {
      idProduto: item.idProduto,
      idLocalizacaoEstoque: this.idLocalizacaoEstoqueOrigem!,
      nomeProduto: item.descricaoProduto,
      skuProduto: item.skuProduto,
      quantidadeTotal: item.quantidade,
      lotes: item.lotes,
    };
    this.dialog.open(LoteSaidaEstoqueModalComponent, { data, width: '820px', maxWidth: '95vw' }).afterClosed().subscribe((result) => {
      if (result?.confirmed) { item.lotes = result.lotes; item.isLoteDetalhado = true; }
    });
  }

  private abrirSeriesSaida(item: TransferenciaItemUI): void {
    const data: SerieSaidaEstoqueModalData = {
      idProduto: item.idProduto,
      idLocalizacaoEstoque: this.idLocalizacaoEstoqueOrigem!,
      nomeProduto: item.descricaoProduto,
      skuProduto: item.skuProduto,
      quantidadeTotal: item.quantidade,
      series: item.series,
    };
    this.dialog.open(SerieSaidaEstoqueModalComponent, { data }).afterClosed().subscribe((result) => {
      if (result?.confirmed) { item.series = result.series; item.isSerieDetalhado = true; }
    });
  }

  public get temErros(): boolean {
    return this.itens.some(i => !!i.erroQtd);
  }

  public salvar(): void {
    if (!this.validar()) return;
    this.isSaving = true;
    const request = this.buildRequest();
    const obs = this.idTransferencia
      ? this.transferenciaEstoqueService.atualizar(this.idTransferencia, request)
      : this.transferenciaEstoqueService.criar(request);
    obs.subscribe({
      next: (resp) => {
        this.isSaving = false;
        if (resp?.isError) { this.alertService.show('Erro', resp.errorDescription, AlertType.error); return; }
        if (!this.idTransferencia) this.idTransferencia = resp.idTransferenciaEstoque;
        this.alertService.show('Sucesso', 'Transferência salva como rascunho.', AlertType.success);
        this.router.navigate(['/suprimentos/transferencia-estoque']);
      },
      error: () => { this.isSaving = false; this.alertService.show('Erro', 'Não foi possível salvar.', AlertType.error); },
    });
  }

  public realizarTransferencia(): void {
    if (!this.validar()) return;
    this.isProcessando = true;
    const request = this.buildRequest();
    const save$ = this.idTransferencia
      ? this.transferenciaEstoqueService.atualizar(this.idTransferencia, request)
      : this.transferenciaEstoqueService.criar(request);

    save$.subscribe({
      next: (saveResp) => {
        if (saveResp?.isError) {
          this.isProcessando = false;
          this.alertService.show('Erro', saveResp.errorDescription, AlertType.error);
          return;
        }
        if (!this.idTransferencia) {
          this.idTransferencia = saveResp.idTransferenciaEstoque;
        }
        this.transferenciaOrquestradorService.processar(this.idTransferencia!).subscribe({
          next: (resp) => {
            this.isProcessando = false;
            if (resp?.isError) { this.alertService.show('Erro', resp.errorDescription, AlertType.error); return; }
            this.alertService.show('Sucesso', 'Transferência realizada com sucesso!', AlertType.success);
            this.router.navigate(['/suprimentos/transferencia-estoque']);
          },
          error: () => { this.isProcessando = false; this.alertService.show('Erro', 'Não foi possível processar a transferência.', AlertType.error); },
        });
      },
      error: () => { this.isProcessando = false; this.alertService.show('Erro', 'Não foi possível salvar antes de processar.', AlertType.error); },
    });
  }

  public cancelar(): void {
    this.router.navigate(['/suprimentos/transferencia-estoque']);
  }

  private validar(): boolean {
    const erros: string[] = [];
    this.itens.forEach(i => (i.erroQtd = undefined));

    if (!this.idLocalizacaoEstoqueOrigem || !this.idLocalizacaoEstoqueDestino) {
      erros.push('Informe as localizações de origem e destino.');
    }
    if (this.idLocalizacaoEstoqueOrigem && this.idLocalizacaoEstoqueDestino
      && this.idLocalizacaoEstoqueOrigem === this.idLocalizacaoEstoqueDestino) {
      erros.push('A origem e o destino devem ser localizações diferentes.');
    }
    if (!this.itens.length) {
      erros.push('Adicione ao menos um produto.');
    }
    if (this.itens.some(i => !i.idProduto)) {
      erros.push('Selecione o produto em todas as linhas adicionadas.');
    }

    for (const item of this.itens) {
      if (!item.idProduto) continue;
      const nome = item.descricaoProduto || `Produto ${item.idProduto}`;

      if (!(item.quantidade > 0)) {
        item.erroQtd = 'Informe uma quantidade maior que zero.';
        erros.push(`${nome}: informe uma quantidade maior que zero.`);
        continue;
      }

      if (item.estoqueOrigem != null && item.quantidade > item.estoqueOrigem) {
        item.erroQtd = `Quantidade maior que o estoque disponível na origem (${item.estoqueOrigem}).`;
        erros.push(`${nome}: quantidade maior que o estoque disponível na origem (${item.estoqueOrigem}).`);
        continue;
      }

      if (item.isLoteDetalhado) {
        if (!item.lotes?.length) {
          item.erroQtd = 'Informe os lotes (produto controla lote).';
          erros.push(`${nome}: informe os lotes (produto controla lote).`);
        } else {
          const soma = item.lotes.reduce((acc, l) => acc + (l.quantidadeSaida || 0), 0);
          if (soma !== item.quantidade) {
            item.erroQtd = `A soma dos lotes (${soma}) difere da quantidade (${item.quantidade}).`;
            erros.push(`${nome}: a soma dos lotes (${soma}) difere da quantidade (${item.quantidade}).`);
          }
        }
      } else if (item.isSerieDetalhado) {
        if ((item.series?.length ?? 0) !== item.quantidade) {
          item.erroQtd = `Informe ${item.quantidade} série(s) (produto controla série).`;
          erros.push(`${nome}: informe ${item.quantidade} série(s) (produto controla série).`);
        }
      }
    }

    if (erros.length) {
      this.alertService.show('Atenção', erros.join('\n'), AlertType.warning);
      return false;
    }
    return true;
  }

  private buildRequest() {
    return {
      idEmpresaOrigem: this.idEmpresaAtual,
      idLocalizacaoEstoqueOrigem: this.idLocalizacaoEstoqueOrigem!,
      idEmpresaDestino: this.idEmpresaAtual,
      idLocalizacaoEstoqueDestino: this.idLocalizacaoEstoqueDestino!,
      observacoes: this.observacoes || undefined,
      itens: this.itens.map(i => ({
        idProduto: i.idProduto,
        quantidade: i.quantidade,
        custoMedio: i.custoMedio,
        isLoteDetalhado: i.isLoteDetalhado,
        isSerieDetalhado: i.isSerieDetalhado,
        // Só o id trafega — código do lote/série vive no cadastro do Produto.
        lotes: i.lotes.map(l => ({ idLote: l.idLote, quantidade: l.quantidadeSaida })),
        series: i.series.map(s => ({ idSerie: s.idSerie })),
      })),
    };
  }
}
